/** Job ids, route builders and parsing shared by every slice. */
import { hooksJobId, liquidityJobId, tokenJobId, type TrackingStatus } from "@/api/types";

export type JobRef =
  | { kind: "token"; chain: string; token: string; id: string }
  | { kind: "liquidity"; chain: string; id: string }
  | { kind: "hooks"; chain: string; id: string };

export function parseJobId(id: string): JobRef | undefined {
  const parts = id.split(":");
  if (parts[0] === "token" && parts.length === 3) {
    return { kind: "token", chain: parts[1]!, token: parts[2]!, id: tokenJobId(parts[1]!, parts[2]!) };
  }
  if (parts[0] === "liquidity" && parts.length === 2)
    return { kind: "liquidity", chain: parts[1]!, id: liquidityJobId(parts[1]!) };
  if (parts[0] === "hooks" && parts.length === 2)
    return { kind: "hooks", chain: parts[1]!, id: hooksJobId(parts[1]!) };
  return undefined;
}

export function tokenRef(chain: string, token: string): JobRef {
  return {
    kind: "token",
    chain: chain.toLowerCase(),
    token: token.toLowerCase(),
    id: tokenJobId(chain, token),
  };
}

/** SPA routes. Keep in sync with src/router.tsx and the server's SearchHit.route. */
export const routes = {
  home: () => "/",
  login: () => "/login",
  overview: (chain: string, token: string) => `/t/${chain}/${token}`,
  tape: (chain: string, token: string) => `/t/${chain}/${token}/tape`,
  family: (chain: string, token: string, address: string) => `/t/${chain}/${token}/family/${address}`,
  holders: (chain: string, token: string) => `/t/${chain}/${token}/holders`,
  entry: (chain: string, token: string) => `/t/${chain}/${token}/entry`,
  flow: (chain: string, token: string) => `/t/${chain}/${token}/flow`,
  /** without `tab`, the token's default tab (lib/charts/lpLayout `defaultTab`) */
  liquidity: (chain: string, token: string, tab?: string) =>
    `/t/${chain}/${token}/liquidity${tab ? `/${tab}` : ""}`,
  ledgers: (chain: string) => `/liquidity/${chain}`,
  ledger: (chain: string, contract: string) => `/liquidity/${chain}/ledger/${contract}`,
  hooks: (chain: string) => `/hooks/${chain}`,
  hook: (chain: string, hook: string) => `/hooks/${chain}/hook/${hook}`,
  signals: () => "/signals",
  alerts: () => "/alerts",
  positions: () => "/positions",
  health: () => "/health",
  compare: () => "/compare",
} as const;

/** Route for a job's home view. */
export function jobHome(ref: JobRef): string {
  switch (ref.kind) {
    case "token":
      return routes.overview(ref.chain, ref.token);
    case "liquidity":
      return routes.ledgers(ref.chain);
    case "hooks":
      return routes.hooks(ref.chain);
  }
}

/** Fill an explorer URL template ("https://basescan.org/tx/{tx}"). */
export function explorerUrl(template: string | undefined, value: string): string | undefined {
  if (!template) return undefined;
  return template.replace("{tx}", value).replace("{address}", value);
}

export function isAddress(s: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(s);
}

export function isTxHash(s: string): boolean {
  return /^0x[0-9a-fA-F]{64}$/.test(s);
}

/** Symbols are display labels; chain and address preserve identity. */
export function tokenLabel(job: { symbol: string; chain: string; token: string }): string {
  return `${job.symbol} · ${job.chain} · ${job.token.slice(0, 6)}…${job.token.slice(-4)}`;
}

/** Preserve token sections; family/address detail and filters belong to the previous token. */
export function switchJobPath(ref: JobRef, pathname: string): string {
  if (ref.kind !== "token") return jobHome(ref);
  const section =
    /^\/t\/[^/]+\/[^/]+\/(tape|holders|entry|flow|liquidity(?:\/(?:book|history|lps))?)\/?$/.exec(
      pathname,
    )?.[1];
  return `${jobHome(ref)}${section ? `/${section}` : ""}`;
}

/** How a token job's sync looks to the reader: one label and one tone, shared by the rail and the token list. */
export interface SyncState {
  /** false when token-flow is not following the token, so its numbers stop at `tracking.last_block` */
  syncing: boolean;
  label: string;
  tone: "ok" | "warn" | "danger" | "neutral";
  /** tooltip: why, and what to do about it */
  detail: string;
}

/** A job without a registry row predates the registry and is assumed to sync. */
export function syncState(tracking: TrackingStatus | undefined): SyncState {
  if (!tracking)
    return { syncing: true, label: "unknown", tone: "neutral", detail: "No tracking record for this token" };
  const since =
    tracking.last_block == null ? "" : `; data stops at block ${tracking.last_block.toLocaleString()}`;
  if (!tracking.enabled)
    return {
      syncing: false,
      label: "disabled",
      tone: "neutral",
      detail: `Not syncing: enabled = false in config.toml${since}. Set it to true and restart token-flow to resume.`,
    };
  // The switch is a request; status is what token-flow last did about it.
  if (tracking.paused && tracking.status !== "paused")
    return {
      syncing: true,
      label: "pausing…",
      tone: "warn",
      detail:
        "Waiting for token-flow to stop this job. It needs a token-flow build that knows the pause switch.",
    };
  if (!tracking.paused && tracking.status === "paused")
    return {
      syncing: true,
      label: "resuming…",
      tone: "warn",
      detail: "Waiting for token-flow to pick this job up again",
    };
  switch (tracking.status) {
    case "paused":
      return {
        syncing: false,
        label: "paused",
        tone: "warn",
        detail: `Not syncing: paused from the token list${since}. Resume catches up from there.`,
      };
    case "live":
      return { syncing: true, label: "live", tone: "ok", detail: "Following new blocks" };
    case "backfilling":
    case "starting":
      return { syncing: true, label: tracking.status, tone: "warn", detail: "Catching up to the chain head" };
    case "complete":
      return { syncing: false, label: "complete", tone: "neutral", detail: `One-shot scan finished${since}` };
    case "retrying":
      return {
        syncing: true,
        label: "retrying",
        tone: "warn",
        detail: tracking.last_error ?? "Retrying after an error",
      };
    case "stopped":
      return {
        syncing: false,
        label: "stopped",
        tone: "neutral",
        detail: `Not syncing: token-flow is not running this job${since}`,
      };
    case "error":
      return {
        syncing: false,
        label: "error",
        tone: "danger",
        detail: tracking.last_error ?? `Job failed${since}`,
      };
  }
}
